import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { useAuth } from '@/context/AuthContext';
import ChatPanel from './components/ChatPanel';
import ImagePanel from './components/ImagePanel';
import { aiPlans } from '@/mocks/ai';

const aiHero =
  'https://readdy.ai/api/search-image?query=Abstract%20artificial%20intelligence%20digital%20brain%20network%20visualization%2C%20glowing%20neural%20connections%2C%20futuristic%20AI%20assistant%20concept%2C%20warm%20amber%20and%20golden%20lighting%2C%20clean%20minimal%20dark%20background%2C%20professional%20editorial%20photography&width=1600&height=800&seq=ai-hero-01&orientation=landscape';

const features = [
  { icon: 'ri-brain-line', title: 'Chat IA Avancé', description: 'Posez n\'importe quelle question. Rédaction, programmation, brainstorming, mathématiques, plans d\'affaires et plus.' },
  { icon: 'ri-image-ai-line', title: 'Génération d\'Images', description: 'Créez des logos, flyers, mockups, illustrations, bannières et visuels marketing en décrivant simplement ce que vous voulez.' },
  { icon: 'ri-code-s-slash-line', title: 'Assistant Code', description: 'Générez, expliquez, déboguez et optimisez du code dans 10+ langages : Python, JavaScript, PHP, Java, C#, SQL, etc.' },
  { icon: 'ri-translate-2', title: 'Traduction & Rédaction', description: 'Traduisez dans n\'importe quelle langue, corrigez la grammaire, réécrivez et rédigez professionnellement.' },
  { icon: 'ri-file-search-line', title: 'Analyse de Documents', description: 'Téléchargez PDF, Word, Excel, PowerPoint. L\'IA résume, extrait les données, répond aux questions et analyse le contenu.' },
  { icon: 'ri-customer-service-2-line', title: 'Assistant Business', description: 'Aide au marketing, SEO, comptabilité, RH, service client, gestion de projet et création de business plans complets.' },
  { icon: 'ri-bookmark-3-line', title: 'Bibliothèque de Prompts', description: 'Des milliers de prompts catégorisés pour business, marketing, éducation, programmation, design, juridique et finance.' },
  { icon: 'ri-voice-ai-line', title: 'Voix & Audio', description: 'Dictée vocale, synthèse vocale, commandes vocales et conversations audio naturelles avec l\'assistant IA.' },
  { icon: 'ri-vip-crown-line', title: 'Modèles Avancés', description: 'Accédez aux modèles les plus puissants avec réponses plus rapides, fichiers volumineux et génération d\'images haute qualité.' },
];

const related = [
  { name: 'Cloud', path: '/cloud', icon: 'ri-cloud-line' },
  { name: 'Work', path: '/work', icon: 'ri-briefcase-4-line' },
  { name: 'Learn', path: '/learn', icon: 'ri-graduation-cap-line' },
  { name: 'Forum', path: '/forum', icon: 'ri-discuss-line' },
];

function AIPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState<'chat' | 'image'>('chat');

  const scrollToWorkspace = () => {
    document.getElementById('ai-workspace')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img src={aiHero} alt="WorldApeiron AI" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/60" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16">
          <div className="max-w-3xl">
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-4">
              WorldApeiron Écosystème
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-foreground-50 mb-4 leading-tight">
              World<span className="text-primary-400">AI</span>
            </h1>
            <p className="text-lg md:text-xl text-foreground-400 mb-3">
              Intelligence Artificielle au service de votre business.
            </p>
            <p className="text-base text-foreground-400 max-w-xl mb-8 leading-relaxed">
              WorldApeiron AI est l'assistant intelligent de l'écosystème. Rédigez, programmez,
              traduisez, générez des images, analysez des documents et boostez votre productivité
              avec une IA de nouvelle génération.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {loading ? (
                <div className="px-8 py-3.5">
                  <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
                </div>
              ) : user ? (
                <button
                  onClick={scrollToWorkspace}
                  className="px-8 py-3.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap text-sm md:text-base"
                >
                  Accéder à mon assistant
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/auth/register')}
                    className="px-8 py-3.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap text-sm md:text-base"
                  >
                    Essayer l'IA gratuitement
                  </button>
                  <button
                    onClick={() => navigate('/auth/login')}
                    className="px-8 py-3.5 border border-foreground-400/50 text-foreground-50 font-semibold rounded-lg hover:bg-foreground-50/10 transition-colors whitespace-nowrap text-sm md:text-base"
                  >
                    Se connecter
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pub */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <AdsBanner pageKey="ai" />
      </section>

      {/* Espace personnel (connecté) */}
      {!loading && user && (
        <section id="ai-workspace" className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="text-center mb-10 md:mb-12">
              <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
                Mon espace
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
                Votre assistant personnel
              </h2>
              <p className="text-foreground-400 max-w-xl mx-auto">
                Discutez avec l'IA ou générez des images en quelques secondes.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200/70">
                  <button
                    type="button"
                    onClick={() => setTab('chat')}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap flex items-center gap-2 ${
                      tab === 'chat'
                        ? 'bg-primary-500 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-50'
                    }`}
                  >
                    <i className="ri-chat-3-line" />
                    Chat
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab('image')}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap flex items-center gap-2 ${
                      tab === 'image'
                        ? 'bg-primary-500 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-50'
                    }`}
                  >
                    <i className="ri-image-ai-line" />
                    Images
                  </button>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                {tab === 'chat' ? <ChatPanel /> : <ImagePanel />}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
              Fonctionnalités
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              Tout ce dont vous avez besoin
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/10 mb-4">
                  <i className={`${feature.icon} text-2xl text-primary-400`} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-50 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
              Forfaits
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              Choisissez votre plan
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {aiPlans.map((plan) => (
              <div
                key={plan.name}
                className={`p-6 rounded-xl border transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-primary-500/10 border-primary-400/50'
                    : 'bg-background-50 border-background-200/70'
                }`}
              >
                <h3 className="font-heading font-semibold text-lg text-foreground-50 mb-1">
                  {plan.name}
                </h3>
                <p className="font-heading font-bold text-2xl text-foreground-50 mb-3">
                  {plan.price}
                </p>
                <p className="text-sm text-foreground-400 mb-4">{plan.description}</p>
                <ul className="space-y-2 mb-5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground-500">
                      <i className="ri-check-line text-primary-400 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate(user ? '/ai#ai-workspace' : '/auth/register')}
                  className={`w-full px-4 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap text-sm ${
                    plan.highlighted
                      ? 'bg-primary-500 text-foreground-950 hover:bg-primary-400'
                      : 'border border-background-300/60 text-foreground-50 hover:bg-background-200/60'
                  }`}
                >
                  Choisir {plan.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Modules */}
      <section className="py-16 md:py-20 bg-background-50 border-t border-background-200/70">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-8 text-center">
            Découvrez aussi
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {related.map((mod) => (
              <button
                key={mod.name}
                onClick={() => navigate(mod.path)}
                className="flex items-center gap-3 px-5 py-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10">
                  <i className={`${mod.icon} text-xl text-primary-400`} />
                </div>
                <span className="font-heading font-semibold text-sm text-foreground-50">
                  {mod.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default AIPage;